"use client";
import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { z } from "zod";
import { useWebMcpTool } from "@/lib/webmcp/react";
import type { WebMcpAction } from "@/lib/webmcp/manifest";
import type { WebMcpResult } from "@/lib/webmcp/types";
import { isWebMcpVisible } from "@/lib/webmcp/visibility";
import { BOOKING_URL, buildWhatsAppContactUrl } from "@/lib/contact-links";
import type { ScenarioInput } from "@/lib/roi-calc/engine";

type Route = { key: string; href: string; legal: boolean };
type FitQuizInput = { audience: string; channels: string[]; timeframe: string };
type RoiInput = Partial<ScenarioInput>;
type ChromeApi = {
  theme: (value: string) => void;
  navigation: (open: boolean) => void;
  recommendServicePath: (input: FitQuizInput) => WebMcpResult;
  calculateResourceEstimate: (input: RoiInput) => WebMcpResult;
};
type ChromeWindow = Window & {
  __seohWebMcpChrome?: ChromeApi;
};
type LinkChoice = { id: string; href: string };
const empty = z.strictObject({});
const blockedPath = /\/(api|client-portal|client-mcp|portal|oauth|auth)(\/|$)/;
function visibleCurrentHref(href: string) {
  const expected = new URL(href, window.location.href).href;
  return Array.from(document.querySelectorAll<HTMLAnchorElement>("a[href]")).some((anchor) =>
    isWebMcpVisible(anchor)
    && new URL(anchor.href, window.location.href).href === expected,
  );
}
function visibleChoiceSignature() {
  const anchors = Array.from(document.querySelectorAll<HTMLAnchorElement>("a[href]"));
  const choices = anchors.map((anchor) => [
    anchor.getAttribute("href") ?? "",
    anchor.hreflang,
    isWebMcpVisible(anchor) ? "visible" : "hidden",
  ].join("\u0000")).join("\u0001");
  const chrome = Boolean((window as ChromeWindow).__seohWebMcpChrome);
  const privacy = Boolean(document.querySelector("[data-cookie-consent-banner][data-cookie-consent-ready]"));
  return `${chrome}\u0002${privacy}\u0002${choices}`;
}
function renderedChromeIsReady() {
  const navbar = document.querySelector("#navbar");
  return !navbar || navbar.getAttribute("data-webmcp-layout-ready") === window.location.pathname;
}
function visibleElement<T extends HTMLElement>(selector: string) {
  if (typeof document === "undefined") return null;
  return Array.from(document.querySelectorAll<T>(selector)).find(isWebMcpVisible) ?? null;
}
function visibleFitChoices(root: HTMLElement | null, group: string) {
  if (!root) return [];
  // Later panels are hidden until earlier answers are chosen; their connected,
  // enabled controls remain state fields of the currently visible quiz.
  return Array.from(root.querySelectorAll<HTMLButtonElement>(`[data-fit-quiz-option][data-fit-quiz-group="${group}"]`))
    .filter((option) => option.isConnected && !option.disabled)
    .map((option) => option.getAttribute("data-fit-quiz-value"))
    .filter((value): value is string => Boolean(value));
}
function enumSchema(values: string[]) {
  return z.enum((values.length ? values : ["unavailable"]) as [string, ...string[]]);
}
function roiNumberSchema(input: HTMLInputElement | null) {
  const minimum = Number(input?.min);
  const maximum = Number(input?.max);
  const stepAttribute = input?.step ?? "";
  const step = stepAttribute === "any" || stepAttribute === "" ? null : Number(stepAttribute);
  if (!input || !Number.isFinite(minimum) || !Number.isFinite(maximum) ||
    (step !== null && (!Number.isFinite(step) || step <= 0))) {
    return z.number().finite().min(1).max(0).optional();
  }
  const range = z.number().finite().min(minimum).max(maximum);
  if (step === null) return range.optional();
  return range.refine((value) => {
    const intervals = (value - minimum) / step;
    return Number.isFinite(intervals) && Math.abs(intervals - Math.round(intervals)) < 1e-8;
  }, "Value does not match the control step").optional();
}
function fitQuizPlannerChoice(root: HTMLElement | null, audiences: string[]) {
  if (!root) return null;
  const anchor = root.querySelector<HTMLAnchorElement>("[data-fit-quiz-planner-cta]");
  if (!anchor || !isWebMcpVisible(anchor)) return null;
  const url = new URL(anchor.href, window.location.href);
  const locale = root.getAttribute("data-fit-locale");
  if (url.origin !== window.location.origin || !locale || url.pathname !== `/${locale}/funnel-planner`) return null;
  const parameters = url.searchParams;
  const allowed = new Set(["from", "band", "audience"]);
  if (parameters.size !== allowed.size || [...parameters.keys()].some((key) => !allowed.has(key))) return null;
  if (parameters.get("from") !== "fit-quiz" || !["starter", "growth", "enterprise"].includes(parameters.get("band") ?? "")) return null;
  const audience = parameters.get("audience");
  if (!audience || !audiences.includes(audience)) return null;
  return { id: "fit_quiz_planner", href: `${url.pathname}${url.search}` };
}
export function WebMcpLinkTool({ action, choices, instance, external = false, requiresRenderedAnchor = true }: { action: WebMcpAction; choices: LinkChoice[]; instance?: string; external?: boolean; requiresRenderedAnchor?: boolean }) {
  const router = useRouter();
  useWebMcpTool({ action, instance, enabled: choices.length > 0,
    schema: z.strictObject({ id: z.enum(choices.length ? choices.map((entry) => entry.id) as [string, ...string[]] : ["unavailable"]) }),
    preview: ({ id }) => choices.find((entry) => entry.id === id)?.href ?? "",
    execute: ({ id }) => {
      const choice = choices.find((entry) => entry.id === id);
      // Rendered choices are capability-bound to their current, visible anchor.
      // The check deliberately runs at invocation, including after human review.
      if (!choice || (requiresRenderedAnchor && (!renderedChromeIsReady() || !visibleCurrentHref(choice.href)))) return { status: "unavailable" };
      if (external) window.location.assign(choice.href); else router.push(choice.href);
      return { status: "success", uiState: "navigation_requested", data: { destination: choice.href, completedExternalAction: false } };
    },
  });
  return null;
}
const LinkTool = WebMcpLinkTool;
export default function WebMcpChrome({ routes, email, phone, personas, mainContentId = "main-content" }: { routes: Route[]; email: string; phone: string; personas: LinkChoice[]; mainContentId?: string }) {
  const pathname = usePathname();
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let signature: string | undefined;
    const refresh = () => {
      if (!renderedChromeIsReady()) {
        clearTimeout(timer);
        signature = undefined;
        setRevision((value) => value === 0 ? value : 0);
        return;
      }
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!renderedChromeIsReady()) {
          signature = undefined;
          setRevision((value) => value === 0 ? value : 0);
          return;
        }
        const nextSignature = visibleChoiceSignature();
        if (signature === nextSignature) return;
        signature = nextSignature;
        setRevision((value) => value + 1);
      }, 80);
    };
    const onLayoutReady = () => {
      if (!renderedChromeIsReady()) return;
      refresh();
    };
    const observer = new MutationObserver(refresh);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["href", "hidden", "aria-hidden", "aria-pressed", "disabled", "class", "style", "open", "data-cookie-consent-ready", "data-webmcp-layout-ready"] });
    window.addEventListener("seoh:webmcp:chrome-ready", refresh);
    window.addEventListener("seoh:webmcp:chrome-layout-ready", onLayoutReady);
    window.addEventListener("resize", refresh);
    window.visualViewport?.addEventListener("resize", refresh);
    refresh();
    return () => {
      clearTimeout(timer); observer.disconnect(); window.removeEventListener("seoh:webmcp:chrome-ready", refresh);
      window.removeEventListener("seoh:webmcp:chrome-layout-ready", onLayoutReady);
      window.removeEventListener("resize", refresh); window.visualViewport?.removeEventListener("resize", refresh);
    };
  }, [pathname]);
  const chrome = typeof window === "undefined" ? undefined : (window as ChromeWindow).__seohWebMcpChrome;
  const privacy = typeof document === "undefined" ? null : document.querySelector<HTMLElement>("[data-cookie-consent-banner][data-cookie-consent-ready]");
  useWebMcpTool({ action: "set_theme", enabled: Boolean(chrome), schema: z.strictObject({ theme: z.enum(["light", "dark"]) }), execute: ({ theme }) => {
    chrome?.theme(theme); return { status: "success", uiState: theme };
  } });
  useWebMcpTool({ action: "open_navigation", enabled: Boolean(chrome), schema: z.strictObject({ open: z.boolean() }), execute: ({ open }) => {
    chrome?.navigation(open); return { status: "success", uiState: "navigation_updated", data: { mobileNavigationOpen: document.body.classList.contains("mobile-nav-active") } };
  } });
  const fitQuizRoot = revision && chrome ? visibleElement<HTMLElement>("[data-fit-quiz]") : null;
  const fitAudience = visibleFitChoices(fitQuizRoot, "audience");
  const fitChannels = visibleFitChoices(fitQuizRoot, "channels");
  const fitTimeframe = visibleFitChoices(fitQuizRoot, "timeframe");
  const fitQuizPlanner = fitQuizPlannerChoice(fitQuizRoot, fitAudience);
  const fitQuizEnabled = Boolean(chrome?.recommendServicePath && fitQuizRoot && fitAudience.length && fitChannels.length && fitTimeframe.length);
  useWebMcpTool({ action: "recommend_service_path", instance: "home_fit_quiz", enabled: fitQuizEnabled,
    schema: z.strictObject({
      audience: enumSchema(fitAudience),
      channels: z.array(enumSchema(fitChannels)).min(1).refine((values) => new Set(values).size === values.length),
      timeframe: enumSchema(fitTimeframe),
    }),
    execute: (input) => {
      if (!chrome?.recommendServicePath || !fitQuizRoot || !isWebMcpVisible(fitQuizRoot)) return { status: "unavailable" };
      return chrome.recommendServicePath(input);
    },
  });
  const roiRoot = revision && chrome ? visibleElement<HTMLElement>("[data-roi-calculator]") : null;
  const roiInput = (name: string) => roiRoot?.querySelector<HTMLInputElement>(`[data-roi-input="${name}"]`) ?? null;
  const monthlyVisitors = roiInput("monthly-visitors");
  const conversionRate = roiInput("conversion-rate");
  const grossValuePerConversion = roiInput("gross-value-per-conversion");
  const monthlyVisitorChange = roiInput("monthly-visitor-change");
  const contributionMargin = roiInput("contribution-margin");
  const monthlyCampaignCost = roiInput("monthly-campaign-cost");
  const monthCount = roiInput("month-count");
  const roiInputs = [
    monthlyVisitors,
    conversionRate,
    grossValuePerConversion,
    monthlyVisitorChange,
    contributionMargin,
    monthlyCampaignCost,
    monthCount,
  ];
  const roiEnabled = Boolean(chrome?.calculateResourceEstimate && roiRoot &&
    roiInputs.every((input) => input && isWebMcpVisible(input) && !input.disabled));
  useWebMcpTool({ action: "calculate_resource_estimate", instance: "home_roi_calculator", enabled: roiEnabled,
    schema: z.strictObject({
      monthlyVisitors: roiNumberSchema(monthlyVisitors),
      visitorToConversionRatePercent: roiNumberSchema(conversionRate),
      grossValuePerConversion: roiNumberSchema(grossValuePerConversion),
      monthlyVisitorChangePercent: roiNumberSchema(monthlyVisitorChange),
      contributionMarginPercent: roiNumberSchema(contributionMargin),
      monthlyCampaignCost: roiNumberSchema(monthlyCampaignCost),
      monthCount: roiNumberSchema(monthCount),
    }),
    execute: (input) => {
      if (!chrome?.calculateResourceEstimate || !roiRoot || !isWebMcpVisible(roiRoot)) return { status: "unavailable" };
      return chrome.calculateResourceEstimate(input);
    },
  });
  useWebMcpTool({ action: "skip_to_content", schema: empty, execute: () => {
    const main = document.getElementById(mainContentId);
    if (!main) return { status: "unavailable" };
    main.tabIndex = -1; main.scrollIntoView({ block: "start" }); main.focus({ preventScroll: true });
    return { status: "success", uiState: "main_content_focused" };
  } });
  useWebMcpTool({ action: "set_consent_preferences", schema: empty, enabled: Boolean(privacy), execute: () => {
    window.dispatchEvent(new CustomEvent("seoh:webmcp:privacy", { detail: "open" }));
    return { status: "review_required", uiState: "privacy_preferences_open", nextActions: ["human_review_and_save"] };
  } });
  useWebMcpTool({ action: "close_privacy_dialog", schema: empty, enabled: Boolean(privacy), execute: () => {
    if (!document.documentElement.hasAttribute("data-cookie-consent-open")) return { status: "unavailable" };
    window.dispatchEvent(new CustomEvent("seoh:webmcp:privacy", { detail: "close" }));
    return { status: "success", uiState: "closed_without_changing_consent" };
  } });
  // Read only code-owned link attributes, never page prose as tool instructions.
  const anchors = revision && typeof document !== "undefined" && renderedChromeIsReady() ? Array.from(document.querySelectorAll<HTMLAnchorElement>("a[href]")) : [];
  const known = new Set(routes.map((route) => route.href.replace(/\/$/, "")));
  const visible = anchors.filter(isWebMcpVisible);
  const internal = visible.flatMap((anchor) => {
    const url = new URL(anchor.href);
    if (url.origin !== location.origin || blockedPath.test(url.pathname) || !known.has(url.pathname.replace(/\/$/, ""))) return [];
    const href = `${url.pathname}${url.search}${url.hash}`;
    // No arbitrary query-driven actions, redirects, share state, or identity parameters.
    if (url.search) return [];
    return [{ id: href, href }];
  });
  const unique = (values: LinkChoice[]) => [...new Map(values.map((value) => [value.id, value])).values()];
  const rendered = (href: string) => visible.some((anchor) => anchor.getAttribute("href") === href);
  const booking = new URL(BOOKING_URL);
  const bookingRendered = visible.some((anchor) => {
    const url = new URL(anchor.href);
    return url.origin === booking.origin && url.pathname === booking.pathname;
  });
  const locales = visible.filter((anchor) => anchor.closest("[data-locale-switcher]") && anchor.hreflang).flatMap((anchor) => {
    const url = new URL(anchor.href);
    return url.origin === location.origin && !url.search && !blockedPath.test(url.pathname) ? [{ id: anchor.hreflang, href: url.pathname }] : [];
  });
  return <>
    <LinkTool action="navigate_site" choices={routes.map(({ key, href }) => ({ id: key, href }))} requiresRenderedAnchor={false} />
    <LinkTool action="open_legal_page" choices={routes.filter((route) => route.legal).map(({ key, href }) => ({ id: key, href }))} requiresRenderedAnchor={false} />
    <LinkTool action="set_locale" choices={unique(locales)} />
    <LinkTool action="select_persona" choices={personas.filter((persona) => rendered(persona.href))} />
    <LinkTool action="follow_cta" choices={unique(internal)} />
    <LinkTool action="follow_cta" instance="home_fit_quiz_planner" choices={fitQuizPlanner ? [fitQuizPlanner] : []} />
    <LinkTool action="start_booking" choices={bookingRendered ? [{ id: "booking", href: BOOKING_URL }] : []} external />
    <LinkTool action="start_email_contact" choices={email && rendered(`mailto:${email}`) ? [{ id: "email", href: `mailto:${email}` }] : []} external />
    <LinkTool action="start_phone_contact" choices={phone && rendered(`tel:${phone}`) ? [{ id: "phone", href: `tel:${phone}` }] : []} external />
    <LinkTool action="start_whatsapp_contact" choices={rendered(buildWhatsAppContactUrl(pathname)) ? [{ id: "contact_handoff", href: buildWhatsAppContactUrl(pathname) }] : []} />
  </>;
}
